import { useMemo, useState } from 'react';
import { EXPLORE_ALL_MARKETPLACES } from 'constants/pages';
import { EventTypesEnum, LessonTypesEnum, SportsEnum } from 'types/generated/client';
import type { ProgramSummary } from './types';
import { ActivityType } from 'components/ActivityBadge';
import { ButtonLink } from 'components/Button';
import LoadingSkeleton from 'components/LoadingSkeleton';
import FilterChips, { ChipOption } from './FilterChips';
import ProgramCard from './ProgramCard';
import { useUpcomingPrograms } from './useUpcomingPrograms';
import SectionEmptyState from '../SectionEmptyState';

interface Props {
  venueId: string;
  /** The club's sports. A club with one sport shows no sport row. */
  sports?: SportsEnum[];
}

interface TypeChipConfig extends ChipOption {
  types: ActivityType[];
  /** Overrides the type match when a chip is decided by more than the activity type. */
  matches?: (program: ProgramSummary) => boolean;
}

const SPORT_CHIPS: (ChipOption & { sport: SportsEnum })[] = [
  {
    key: 'tennis',
    label: 'Tennis',
    iconSrc: '/images/programming/tennis.png',
    sport: SportsEnum.Tennis,
  },
  {
    key: 'pickleball',
    label: 'Pickleball',
    iconSrc: '/images/programming/pickleball.png',
    sport: SportsEnum.Pickleball,
  },
  {
    key: 'padel',
    label: 'Padel',
    iconSrc: '/images/programming/padel.png',
    sport: SportsEnum.Padel,
  },
];

const TYPE_CHIPS: TypeChipConfig[] = [
  {
    key: 'open-play',
    label: 'Open play',
    iconSrc: '/images/programming/open-play.png',
    types: [EventTypesEnum.OpenPlay, LessonTypesEnum.OpenPlay],
  },
  {
    key: 'clinic',
    label: 'Clinic',
    iconSrc: '/images/programming/clinic.png',
    types: [LessonTypesEnum.Clinic],
  },
  {
    key: 'fitness',
    label: 'Fitness',
    iconSrc: '/images/programming/fitness.png',
    types: [LessonTypesEnum.Cardio, LessonTypesEnum.Custom],
  },
  {
    key: 'camp',
    label: 'Camp',
    iconSrc: '/images/programming/camp.png',
    types: [LessonTypesEnum.Camp],
  },
  {
    key: 'rfx',
    label: 'RFX',
    iconSrc: '/images/programming/rfx.png',
    types: [LessonTypesEnum.Rfx],
    matches: (program) => !!program.isRfx || program.activityType === LessonTypesEnum.Rfx,
  },
  {
    key: 'social',
    label: 'Social',
    iconSrc: '/images/programming/socials.png',
    types: [EventTypesEnum.Social, EventTypesEnum.GeneralEvent],
    matches: (program) =>
      !program.isRfx &&
      [EventTypesEnum.Social, EventTypesEnum.GeneralEvent].includes(
        program.activityType as EventTypesEnum,
      ),
  },
  {
    key: 'tournament',
    label: 'Tournament',
    iconSrc: '/images/player-home-v2/search-bar-icons/tournament-gradient.svg',
    types: [EventTypesEnum.Tournament],
  },
  {
    key: 'league',
    label: 'League',
    iconSrc: '/images/player-home-v2/search-bar-icons/league-gradient.png',
    types: [EventTypesEnum.League],
  },
];

export default function UpcomingPrograms({ venueId, sports }: Props) {
  const { programs, isLoading } = useUpcomingPrograms({ venueId });
  // Offering one sport makes the row a label rather than a filter, so it is dropped.
  const sportChips = sports
    ? SPORT_CHIPS.filter((chip) => sports.includes(chip.sport))
    : SPORT_CHIPS;
  const [selectedSportKey, setSelectedSportKey] = useState<string | null>(null);
  const [selectedTypeKey, setSelectedTypeKey] = useState<string | null>(null);

  const filteredPrograms = useMemo(() => {
    const sportChip = SPORT_CHIPS.find((chip) => chip.key === selectedSportKey);
    const typeChip = TYPE_CHIPS.find((chip) => chip.key === selectedTypeKey);

    return programs.filter((program) => {
      if (sportChip && program.sport !== sportChip.sport) {
        return false;
      }
      const isTypeMatch = typeChip?.matches
        ? typeChip.matches(program)
        : typeChip?.types.includes(program.activityType);
      if (typeChip && !isTypeMatch) {
        return false;
      }
      return true;
    });
  }, [programs, selectedSportKey, selectedTypeKey]);

  return (
    <section className="flex flex-col gap-ds-lg">
      {/* Mobile reaches this through the Events tab, which already names the section. */}
      <h2 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden">
        Upcoming programs
      </h2>
      {sportChips.length > 1 && (
        <FilterChips
          options={sportChips}
          selectedKey={selectedSportKey}
          onSelect={setSelectedSportKey}
        />
      )}
      <FilterChips
        options={TYPE_CHIPS}
        selectedKey={selectedTypeKey}
        onSelect={setSelectedTypeKey}
      />
      {isLoading ? (
        <LoadingSkeleton
          count={3}
          height="11.5rem"
          borderRadius="1rem"
          containerClassName="flex flex-col gap-ds-lg"
        />
      ) : filteredPrograms.length === 0 ? (
        <SectionEmptyState
          message="No upcoming programs."
          hint="It’s quiet right now. Try another activity or come back later."
        />
      ) : (
        <div className="flex flex-col gap-ds-lg">
          {filteredPrograms.map((program) => (
            <ProgramCard key={program.id} program={program} />
          ))}
        </div>
      )}
      <div className="flex">
        <ButtonLink href={EXPLORE_ALL_MARKETPLACES} variant="brand" size="md" isInline>
          View all programs
        </ButtonLink>
      </div>
    </section>
  );
}
